/**
 * Home on Mojo Up AI Cloud for an organisation without Teams (cloudPlan.ts): a person's own space is their account
 * (sign-in for AI Workbench, its licence and updates, and the downloads), and a free team is read-only until it moves
 * to Teams. So Home is the few things an account does, and the way to Teams; never a workspace's setup checklist.
 */

import { ArrowDownToLine, CircleQuestion, Display, House, SquarePlus } from '@gravity-ui/icons';
import { useNavigate } from 'react-router-dom';

import { routes } from '../api/client.ts';
import { useCloud } from '../auth/AuthProvider.tsx';
import { useCloudStanding } from '../cloudPlan.ts';
import { UpgradeToTeamsCard } from '../components/cloud/UpgradeToTeams.tsx';
import { Page, PageHeader } from '../components/ui.tsx';
import { ArtSuggestion, VIEW_TONES } from '../components/ui/Art.tsx';
import { useLoad } from '../hooks.ts';

const tone = VIEW_TONES.home!;

export function CloudAccountHome() {
  const navigate = useNavigate();
  const cloud = useCloud();
  const { personal } = useCloudStanding();
  const devices = useLoad(() => routes.devices().catch(() => undefined), []);
  const first = cloud?.me.name?.split(' ')[0];
  const signedIn = devices.data?.length ?? 0;
  return (
    <Page>
      <PageHeader
        tone={tone}
        icon={<House />}
        title={personal ? (first ? `Welcome, ${first}` : 'Your account') : (cloud?.organisation.name ?? 'Your team')}
        subtitle={
          personal
            ? 'Your AI Cloud account. Sign in to AI Workbench with it for its licence and updates.'
            : 'This team is on the free plan. Its work is kept safe, read-only, until you start Teams.'
        }
      />
      {/* Four things to try, re-rowed by the column's width (two, then four abreast), not the window's. */}
      <div className="grid gap-3 @[34rem]:grid-cols-2 @[60rem]:grid-cols-4">
        <ArtSuggestion tone={tone} icon={<ArrowDownToLine />} onPress={() => navigate('/downloads')}>
          <span className="block">Download AI Workbench</span>
          <span className="block text-xs font-normal text-muted">The desktop app and the node, for Windows, macOS and Linux.</span>
        </ArtSuggestion>
        <ArtSuggestion tone={tone} icon={<SquarePlus />} onPress={() => navigate('/enrol')}>
          <span className="block">Sign in a workbench</span>
          <span className="block text-xs font-normal text-muted">Enter the code AI Workbench shows when it asks you to sign in.</span>
        </ArtSuggestion>
        <ArtSuggestion tone={tone} icon={<Display />} onPress={() => navigate('/workbenches')}>
          <span className="block">Your workbenches</span>
          <span className="block text-xs font-normal text-muted">{devices.data ? (signedIn ? `${signedIn} signed in with this account.` : 'None signed in yet.') : 'The machines signed in with this account.'}</span>
        </ArtSuggestion>
        <ArtSuggestion tone={tone} icon={<CircleQuestion />} onPress={() => navigate('/help')}>
          <span className="block">Help and support</span>
          <span className="block text-xs font-normal text-muted">Ask us about your account, billing or a problem.</span>
        </ArtSuggestion>
      </div>
      <UpgradeToTeamsCard tone="blue" />
    </Page>
  );
}
